import { formatArmBudgetReference, formatCatalogBaseReference, formatItemDimensionCm } from '@utils/budget/formatPieceDimensions'
import { pickSelectedFabricTexture, resolveFabricPanelLabels } from '@utils/budget/fabricCharacteristics'
import { localizeCatalogLabel, resolveCornerXlOrejaDisplayLabel } from '@utils/i18n/catalogLabels'
import { getDimensionAbbrevLabels, ui } from '@utils/i18n/uiCopy'
import { isRelaxIntegratedUnitByDefaults } from '@utils/atemporal/relaxArmVariants'
import { parseSeatHeightFromCatalog } from '@utils/atemporal/seatHeightCatalog'
import { resolveSelectedLinkedUnit } from '@utils/sceneObjects/items/duplicateLinkedUnit'
import { AnimatePresence, motion } from 'framer-motion'
import type { AnyID, LinkedItem } from '@types'
import { useMemo } from 'react'

type SelectedUnitInfoProps = {
  items: Map<AnyID, any>
  selectedItems: Set<string> | Set<AnyID>
  currentObjectID?: AnyID | string | ''
}

const isArmCatalog = (catalogID: unknown) => /^BR\d+(-\d+)?$/i.test(String(catalogID || ''))

const isRelaxUnit = (item: any) =>
  isRelaxIntegratedUnitByDefaults(item?.catalogID || item?.referencia)

const normalizeReference = (item: any) => {
  const raw = String(item?.referencia || item?.catalogID || '').trim()
  if (!raw) return '—'
  if (isArmCatalog(raw)) return formatArmBudgetReference(raw) || '—'
  return formatCatalogBaseReference(raw)
}

/** Descripción visible: Relax → «Relax»; Corner XL Oreja → oreja izq/der. */
const normalizeSubtitle = (item: any, title: string) => {
  if (isRelaxUnit(item)) return 'Relax'
  const orejaLabel = resolveCornerXlOrejaDisplayLabel(item?.catalogID || item?.referencia)
  if (orejaLabel) return orejaLabel
  const description = String(item?.description || '').trim()
  if (!description || description === title) return null
  return localizeCatalogLabel(description)
}

const resolveSelectedSceneItem = (
  items: Map<AnyID, any>,
  selectedItems: Iterable<string | AnyID>,
  currentObjectID?: AnyID | string | '',
): LinkedItem | null => {
  const unit = resolveSelectedLinkedUnit(items, selectedItems, currentObjectID)
  if (unit) return unit

  const ids = [
    ...(currentObjectID ? [String(currentObjectID)] : []),
    ...Array.from(selectedItems, String),
  ]
  for (const id of ids) {
    const item = items.get(id as AnyID)
    if (item?.type === 'linkedItem' && item.universal === false) return item as LinkedItem
  }
  return null
}

function DimMetric({ label, value }: { label: string; value: string }) {
  return (
    <span className='inline-flex items-baseline gap-1'>
      <span className='text-[9px] font-medium uppercase tracking-[0.14em] text-[#9a9a9a] min-[2400px]:text-[11px]'>
        {label}
      </span>
      <span className='font-century text-[12px] tabular-nums tracking-wide text-[#3a3a3a] min-[2400px]:text-[15px]'>
        {value}
        <span className='ml-0.5 text-[10px] font-normal text-[#8a8a8a] min-[2400px]:text-[12px]'>
          cm
        </span>
      </span>
    </span>
  )
}

/**
 * Chip discreto con la referencia y cotas de la unidad seleccionada.
 * Pensado para convivir con el toolbar sin competir por atención.
 *
 * Meta (alineado con Kube main):
 * 1) Asiento X cm
 * 2) Grupo de familia (p. ej. Algodón)
 * 3) Familia del tejido seleccionado (p. ej. Mild Cotton)
 */
export function SelectedUnitInfo({
  items,
  selectedItems,
  currentObjectID,
}: SelectedUnitInfoProps) {
  const selected = useMemo(
    () => resolveSelectedSceneItem(items, selectedItems, currentObjectID),
    [items, selectedItems, currentObjectID],
  )

  const dimensionLabels = getDimensionAbbrevLabels()
  const info = useMemo(() => {
    if (!selected) return null

    const reference = normalizeReference(selected)
    const isArm = isArmCatalog(selected.catalogID)
    const title = isArm ? `${ui('arm')} ${reference}` : reference
    const subtitle = isArm ? null : normalizeSubtitle(selected, title)

    const largo = formatItemDimensionCm(selected.width)
    const fondo = formatItemDimensionCm(selected.thickness)
    const alto = formatItemDimensionCm(selected.height)

    const seatHeight = !isArm
      ? Number((selected as any).seatHeightCm) ||
        parseSeatHeightFromCatalog(selected.catalogID) ||
        null
      : null

    const textures = Array.isArray(selected.textures) ? selected.textures : []
    const fabricTexture = pickSelectedFabricTexture(textures)

    const fabricLabels = resolveFabricPanelLabels(fabricTexture as any)
    const fabricUrl = String(fabricTexture?.url || '').trim()

    return {
      id: String(selected.id),
      title,
      subtitle,
      largo,
      fondo,
      alto,
      isArm,
      seatHeight,
      fabricGroup: fabricLabels.group,
      fabricSubgroup: fabricLabels.subgroup,
      fabricUrl: fabricUrl ? encodeURI(fabricUrl) : '',
    }
  }, [selected])

  return (
    <AnimatePresence mode='wait'>
      {info ? (
        <motion.div
          key={info.id}
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          className='pointer-events-none max-w-[min(420px,46vw)] select-none'
        >
          <div className='border-colorAtemporal rounded-2xl border bg-white px-3.5 py-2.5 shadow-lg min-[480px]:px-4 min-[480px]:py-3 min-[2400px]:rounded-3xl min-[2400px]:px-6 min-[2400px]:py-4'>
            <div className='mb-1.5 flex items-center gap-2 min-[2400px]:mb-2'>
              <span className='h-px w-3 bg-colorAtemporal/70 min-[2400px]:w-4' />
              <p className='text-[9px] font-medium uppercase tracking-[0.22em] text-colorAtemporal min-[2400px]:text-[11px]'>
                {ui('selection')}
              </p>
            </div>

            <div className='flex min-w-0 items-start gap-3'>
              {info.fabricUrl ? (
                <span
                  className='mt-0.5 h-9 w-9 shrink-0 rounded-lg border border-[#e6e0d2] bg-cover bg-center shadow-inner min-[2400px]:h-12 min-[2400px]:w-12 min-[2400px]:rounded-xl'
                  style={{ backgroundImage: `url('${info.fabricUrl}')` }}
                  aria-hidden
                />
              ) : null}

              <div className='min-w-0 flex-1'>
                {info.subtitle ? (
                  <p className='mt-0.5 truncate text-[15px] font-semibold leading-snug text-[#2c2c2c] min-[2400px]:text-[19px]'>
                    {info.subtitle}
                  </p>
                ) : null}
                <p className='font-century truncate text-[15px] font-semibold leading-tight tracking-[0.01em] text-[#2c2c2c] min-[2400px]:text-[19px]'>
                  {ui('refPrefix')} {info.title} 
                </p>

                {!info.isArm ? (
                  <div className='mt-2 flex flex-wrap items-center gap-x-2.5 gap-y-1 min-[2400px]:mt-2.5 min-[2400px]:gap-x-3.5'>
                    <DimMetric label={dimensionLabels.largo} value={info.largo} />
                    <span className='h-3 w-px bg-[#e5e0d4]' aria-hidden />
                    <DimMetric label={dimensionLabels.fondo} value={info.fondo} />
                    <span className='h-3 w-px bg-[#e5e0d4]' aria-hidden />
                    <DimMetric label={dimensionLabels.alto} value={info.alto} />
                  </div>
                ) : null}

                {info.seatHeight ? (
                  <p className='mt-1.5 text-[10.5px] leading-snug tracking-[0.01em] text-[#8d8d8d] min-[2400px]:mt-2 min-[2400px]:text-[13px]'>
                    {ui('seat')} {info.seatHeight} cm
                  </p>
                ) : null}

                {info.fabricGroup || info.fabricSubgroup ? (
                  <div
                    className='text-[10.5px] leading-snug tracking-[0.01em] text-[#8d8d8d] min-[2400px]:mt-2 min-[2400px]:text-[13px]'
                    title={[info.fabricGroup, info.fabricSubgroup].filter(Boolean).join(' · ')}
                  >
                    {info.fabricGroup ? (
                      <p className='truncate font-semibold tracking-[0.01em] text-[#6f6f6f]'>
                        {info.fabricGroup}
                      </p>
                    ) : null}
                    {info.fabricSubgroup ? (
                      <p className='line-clamp-2'>{info.fabricSubgroup}</p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  )
}
